import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUIStore } from '@/lib/core/stores/useUIStore';
import { formatUtcDateAndHour, randomUuid } from '@/lib/core/utils';
import { LinkRouteUrl } from '@/lib/core/utils';
import {
  BookFlowExperiencesPropertiesModel,
  ExperiencesBookFlowWhichElementModelContentType,
  ExperienceBookFlowConfirmationElementContentModelContentType,
  ExperienceBookFlowConfirmationPropertiesModel,
  ExperienceBookFlowExperienceQuantityConfirmationElementContentModelContentType,
  ExperienceBookFlowExperienceQuantityConfirmationPropertiesModel,
  ExperienceBookFlowMoreDetailsElementContentModelContentType,
  ExperienceBookFlowMoreDetailsPropertiesModel,
  ExperienceBookFlowWhatWhenHowManyWhichElementContentModelContentType,
  ExperienceBookFlowWhatWhenHowManyWhichPropertiesModel,
  ExperiencesBookFlowServiceTypePropertiesModel,
  ReservationDetailDto,
  SeatingReservationDto,
} from '@/services/platform/api/model';
import type { BookingState, BookFlowExperienceProps } from './types';
import { ExperienceBook } from './book';
import {
  buildMewsReservationGroupRequest,
  getMewsBookingEngineConfiguration,
  mapMewsCategoryDtoToItem,
  MewsAgeConfigDto,
  MewsAllocation,
  MewsCategory,
  MewsCategoryItem,
  mewsPricePeriodKey,
  mewsServiceIsSingleOccupancy,
  mewsServiceUsesHourSlots,
  mewsUnitLabelKey,
  useMewsCategories,
  useMewsCreateReservationGroup,
  useMewsMultiCategoryAllocation,
} from '@/services/platform/data/booking/mews';
import { useIntl } from 'react-intl';
import { useClientTranslation } from '@/lib/core/i18n';
import { useAuthStore } from '@/lib/core/stores';
import { Toast } from '@/components/ui/toast';
import { SpinLoading } from '@/components/shared/loadings/spin';
import { getReservation } from '@/services/platform/data/booking';
import {
  MewsPaymentStep,
  forgetCheckout,
  recallCheckout,
  rememberCheckout,
  reviveMewsBooking,
  type MewsCheckoutState,
  type MewsPaidState,
} from '@/components/shared/checkout';
import { PaymentStepContentTypes, hasStep, type FlowSteps } from '../shared/steps';

/* Seven Rooms */

import { EatAndDrinkDetails as SevenRoomsDetails } from '../eat-and-drink/details';
import { ConfirmationRequest as SevenRoomsConfirmationRequest } from '../eat-and-drink/confirmation/request';
import { ConfirmationBook as SevenRoomsConfirmationBook } from '../eat-and-drink/confirmation/book';

/* Mews */

import type { WhichCmsProps } from '../shared/mews/types';
import { MewsWhichPage } from '../shared/mews/sheets/which';
import { MewsRateAndDistribution } from '../shared/mews/rate';
import { MewsMoreDetails } from '../shared/mews/details';
import { MewsConfirmation } from '../shared/mews/confirmation';
import { experienceLabels } from '../shared/labels';

import { buildInitialAllocations } from '../shared/mews/allocations';

export type { BookingState, BookFlowExperienceProps };

/**
 * This flow's steps are nested one level deeper than every other one: `serviceTypeSteps` holds a
 * block per service type, each with its own `steps`. Which one applies is only known once the
 * customer has picked a service in step 0.
 *
 * Taken as a function of the service type rather than read off component state so it can also be
 * asked about a service type that state does not hold yet — which is exactly the situation after a
 * redirect-based payment cold-reloads the app and the booking is still being revived.
 */
function resolveServiceTypeSteps(
  serviceTypeSteps: FlowSteps,
  serviceType: string | null | undefined
): FlowSteps {
  const match = (serviceTypeSteps ?? []).find(
    type =>
      (type?.content?.properties as ExperiencesBookFlowServiceTypePropertiesModel)?.serviceType == serviceType
  );
  return (match?.content?.properties as ExperiencesBookFlowServiceTypePropertiesModel)?.steps?.items ?? [];
}

const makeEmptyBooking = (): BookingState => ({
  service: null,
  venue: null,
  occurrence: null,
  notes: '',
  /* Seven Rooms */
  guestCount: null,
  date: null,
  room: null,
  time: null,
  isRequestAnyway: false,
  groupNotes: {},
  selectedTags: [],
  selectedGroupTags: [],
  idempotencyKey: randomUuid(),
  seatingAreaPublicGuid: undefined,
  /* Mews */
  dateFrom: null,
  dateTo: null,
  adults: 0,
  children: 0,
  category: null,
  allocations: [],
  paymentMethod: null,
  extras: [],
  voucherCode: '',
});

// ── BookFlowExperience ────────────────────────────────────────────────────────

export function BookFlowExperience({ content, context }: BookFlowExperienceProps) {
  const t = useClientTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore(state => state.user);
  const { locale } = useIntl();
  const setHideNavbar = useUIStore(state => state.setHideNavbar);
  // `number`, not a union of positions: with the payment step authored, how far the indices run
  // depends on the service type's own `steps`, so they cannot be enumerated here.
  const [step, setStep] = useState<number>(0);
  const [reservation, setReservation] = useState<SeatingReservationDto | null>(null);
  /** What the payment step is charging for — set on the way into it, from either entry point. */
  const [pendingPayment, setPendingPayment] = useState<{ reservationGuid: string; amount: number } | null>(
    null
  );
  // Kept separate from `reservation` (the SevenRooms shape) rather than widening that union —
  // `ReservationDetailDto` and `SeatingReservationDto` only "work" interchangeably there today
  // because every field on both happens to be optional; a dedicated state avoids relying on that.
  const [mewsReservation, setMewsReservation] = useState<ReservationDetailDto | null>(null);
  const [errorToastMessage, setErrorToastMessage] = useState<string | null>(null);
  const [errorToastKey, setErrorToastKey] = useState(0);

  const stepTypes = content.properties?.serviceTypeSteps?.items ?? [];

  /**
   * A payment left in flight by a redirect-based method (MB WAY sends the customer away and back
   * via `return_url`, which is this flow's own URL once payment is a step here — so the whole app
   * cold-reloads). `session.ts` already persisted everything needed to rebuild the payment step.
   *
   * The "is a payment step even authored" test has to sweep every service type, not just the booked
   * one: `booking.service` is empty on a cold reload, so the flow does not yet know which service
   * type's `steps` apply. The `returnPath` match is what keeps this scoped to this flow.
   */
  const recalledCheckout = useMemo(() => {
    // `mewsPaid` means the /checkout route already handled this payment and is handing the booking
    // back — its own effect below owns that, and both running would double-resume.
    if ((location.state as { mewsPaid?: MewsPaidState } | null)?.mewsPaid) return undefined;
    const anyServiceTypeTakesPayment = stepTypes.some(type =>
      hasStep(
        (type?.content?.properties as ExperiencesBookFlowServiceTypePropertiesModel)?.steps?.items,
        PaymentStepContentTypes.Experience
      )
    );
    if (!anyServiceTypeTakesPayment) return undefined;

    const recalled = recallCheckout('mews');
    return recalled?.returnPath === location.pathname ? recalled : undefined;
  }, [stepTypes, location.state, location.pathname]);

  // Resuming, step 0 (What/When/HowMany/Which) would otherwise flash for the length of the revive
  // and the `getReservation` fetch below — true only for the resume cases; every other entry shows
  // the book flow immediately as it always has. Mirrors `flows/event/index.tsx`'s `isPreselecting`.
  const [isResuming, setIsResuming] = useState(() => {
    const state = location.state as { mewsPaid?: MewsPaidState } | null;
    return !!state?.mewsPaid || !!recalledCheckout;
  });

  useEffect(() => {
    setHideNavbar(true);
    return () => setHideNavbar(false);
  }, [setHideNavbar]);
  const [booking, setBooking] = useState<BookingState>(makeEmptyBooking);

  // Paid via checkout: the reservation already exists and is now `Confirmed` — rehydrate the
  // booking exactly as it was before the redirect and fetch the authoritative reservation, rather
  // than replaying the booking steps (which would create a second reservation). Merged onto `prev`
  // rather than replacing it outright: `reviveMewsBooking` only knows about the Mews-shaped fields,
  // and `booking` here also carries this flow's SevenRooms-only fields (service, guestCount, ...).
  const hasResumed = useRef(false);
  useEffect(() => {
    const state = location.state as { mewsPaid?: MewsPaidState } | null;
    const paid = state?.mewsPaid;
    if (!paid || hasResumed.current) return;
    hasResumed.current = true;

    setBooking(prev => ({ ...prev, ...reviveMewsBooking(paid.booking) }));
    getReservation(paid.reservationGuid, locale).then(data => {
      if (data) setMewsReservation(data);
      setStep(3);
      setIsResuming(false);
    });
  }, [location.state, locale]);

  // Reloaded mid-payment on the inline path. Where the branch above trusts its router state to mean
  // "paid", this one re-reads the reservation and lets its status decide: still held → back onto the
  // payment step, where `CheckoutScreen`'s own `getActivePayments` recovery picks the stalled
  // payment up rather than starting a second one; already `Confirmed` (the webhook won the race) →
  // straight to the confirmation.
  //
  // Both target steps are resolved from the *recalled* booking's service type rather than from
  // `steps` above, which is still empty at this point — `booking.service` only comes back with the
  // `setBooking` on the line below, one render later.
  useEffect(() => {
    if (!recalledCheckout || hasResumed.current) return;
    hasResumed.current = true;

    const revived = reviveMewsBooking(recalledCheckout.booking) as BookingState;
    setBooking(prev => ({ ...prev, ...revived }));

    const revivedSteps = resolveServiceTypeSteps(stepTypes, revived.service?.serviceType) ?? [];
    const revivedPaymentStep = revivedSteps.findIndex(
      s => s.content?.contentType === PaymentStepContentTypes.Experience
    );
    const revivedConfirmationStep = revivedSteps.findIndex(
      s =>
        s.content?.contentType ===
        ExperienceBookFlowConfirmationElementContentModelContentType.experienceBookFlowConfirmation
    );

    getReservation(recalledCheckout.reservationGuid, locale).then(data => {
      if (data) setMewsReservation(data);

      // A read that failed is not evidence the payment landed — `getReservation` returns undefined
      // on a 401, a 404 or a dropped connection — so it counts as unpaid rather than as confirmed.
      const stillUnpaid = !data || (data.status ?? '').toLowerCase() === 'awaitingpayment';

      if (stillUnpaid && revivedPaymentStep !== -1) {
        setPendingPayment({
          reservationGuid: recalledCheckout.reservationGuid,
          amount: recalledCheckout.amountMajor,
        });
        setStep(revivedPaymentStep);
      } else if (stillUnpaid) {
        // The gate above accepts a session as soon as *any* service type takes payment inline, but
        // the one actually booked may not — it will have gone out to `/checkout` instead. Nothing
        // here can charge for it, so leave the flow at its start and keep the breadcrumb for that
        // route, rather than confirming a booking nobody has paid for.
        setStep(0);
      } else {
        // Nothing left to pay — and nothing left to recover from, so the breadcrumb goes too.
        forgetCheckout();
        setStep(revivedConfirmationStep !== -1 ? revivedConfirmationStep : 3);
      }
      setIsResuming(false);
    });
  }, [recalledCheckout, stepTypes, locale]);

  const steps = resolveServiceTypeSteps(stepTypes, booking?.service?.serviceType) ?? [];

  // Unlike sunbed and stay, `step` here *is* the index into `steps` (0 and 0.1 aside), so both of
  // these have to be looked up rather than hardcoded — authoring a payment step shifts the
  // confirmation along with it.
  const paymentStepIndex = steps.findIndex(
    s => s.content?.contentType === PaymentStepContentTypes.Experience
  );
  const confirmationStepIndex = steps.findIndex(
    s =>
      s.content?.contentType ===
      ExperienceBookFlowConfirmationElementContentModelContentType.experienceBookFlowConfirmation
  );
  const confirmationStep = confirmationStepIndex !== -1 ? confirmationStepIndex : 3;

  const handleBack = () => {
    if (step === 0) {
      navigate(-1);
    } else if (step === 0.1 && booking?.service?.provider?.toLowerCase() == 'mews') {
      setStep(0);
    } else if (step === 1 && booking?.service?.provider?.toLowerCase() == 'mews') {
      setStep(0.1);
    } else {
      setStep(prev => prev - 1);
    }
  };

  const handleNext = () => {
    if (step == 0 && booking?.service?.provider?.toLowerCase() == 'mews') {
      setStep(0.1);
    } else {
      setStep(prev => prev + 1);
    }
  };

  /**
   * Payment landed. The reservation is `Confirmed` server-side by now, so the confirmation screen
   * renders from a fresh read rather than from the pre-payment copy already in state.
   *
   * `CheckoutScreen` has already called `forgetCheckout()` before this runs.
   */
  const handlePaid = () => {
    if (!pendingPayment) return;
    getReservation(pendingPayment.reservationGuid, locale).then(data => {
      if (data) setMewsReservation(data);

      // The money has landed and the session is already forgotten, so there is nothing left to
      // resume — but with neither a fresh read nor an earlier one there is also nothing for the
      // confirmation to render. Say so instead of stepping onto a screen that paints nothing.
      if (!data && !mewsReservation) {
        setErrorToastKey(key => key + 1);
        setErrorToastMessage(t('App.Labels.BookingError'));
        return;
      }

      setPendingPayment(null);
      setStep(confirmationStep);
    });
  };

  const handleRequestSuccess = (res: SeatingReservationDto) => {
    setReservation(res);
    setStep(2);
  };

  /* Mews */

  const guestsMax = booking.venue?.guestsMax ?? undefined;
  const enterpriseId = booking.venue?.externalId ?? undefined;
  const serviceId = booking.venue?.bookableServiceExternalId ?? undefined;

  // ── Age configuration ────────────────────────────────────────────────────────
  const [ageConfig, setAgeConfig] = useState<MewsAgeConfigDto | undefined>(undefined);
  useEffect(() => {
    if (!serviceId || !enterpriseId || booking?.service?.provider?.toLowerCase() != 'mews') return;
    getMewsBookingEngineConfiguration(serviceId, enterpriseId).then(setAgeConfig);
  }, [serviceId, enterpriseId]);

  const {
    fetch: fetchCategories,
    categories,
    isLoading: categoriesLoading,
  } = useMewsCategories({
    serviceId,
    enterpriseId,
    dateFrom: booking.dateFrom,
    dateTo: booking.dateTo,
    adults: booking.adults,
    children: booking.children,
    locale,
    ageConfig,
    mapDto: dto => mapMewsCategoryDtoToItem(dto, priceUnitLabel),
  });

  const { allocate, isPending: allocationPending } = useMewsMultiCategoryAllocation({
    serviceId,
    enterpriseId,
    dateFrom: booking.dateFrom,
    dateTo: booking.dateTo,
    ageConfig,
  });

  // Re-fetch categories whenever date, guest count, serviceId, or ageConfig availability changes.
  const hasAgeConfig = !!ageConfig;
  useEffect(() => {
    if (!booking.dateFrom || !booking.dateTo || booking.adults <= 0 || !serviceId) return;
    fetchCategories();
  }, [booking.dateFrom, booking.dateTo, booking.adults, booking.children, serviceId, locale, hasAgeConfig]);

  // Extract which-step CMS props from step0's bookForm

  const bookStep = steps?.find(
    s =>
      s?.content?.contentType ==
      ExperienceBookFlowWhatWhenHowManyWhichElementContentModelContentType?.experienceBookFlowWhatWhenHowManyWhich
  )?.content?.properties as ExperienceBookFlowWhatWhenHowManyWhichPropertiesModel;
  const formItems = (bookStep?.steps?.items ?? []).map(
    item => item.content //properties as ExperienceBookFlowFormItemPropertiesModel | undefined
  );
  const whichFormItem = formItems.find(
    item => item?.contentType === ExperiencesBookFlowWhichElementModelContentType.experiencesBookFlowWhich
  );
  const whichProps = whichFormItem?.properties as WhichCmsProps | undefined;

  // Names the slot a booking was made for, on the screens that summarise it — the rate step, the
  // details step and the confirmation. Deliberately not on the category step, which sits directly
  // after the When sheet and would only repeat the answer the customer just gave.
  //
  // Only hourly services carry it: for a Day service the dates are already spelled out.
  const whenLabel =
    mewsServiceUsesHourSlots(ageConfig) && booking.dateFrom
      ? formatUtcDateAndHour(booking.dateFrom)
      : undefined;

  // A unit here is a massage or a court, never a sunbed — and the price is quoted per time unit, so
  // an hourly service must not read "daily". Both are resolved once and handed to the sunbed screens
  // this flow borrows.
  const priceUnitLabel = t('App.Labels.PriceUnit', {
    unit: t(mewsUnitLabelKey(booking.service?.serviceType), { count: 1 }),
    period: t(mewsPricePeriodKey(ageConfig)),
  });

  const handleContinueFromWhich = (category: MewsCategory) => {
    const allocations = buildInitialAllocations(category, booking.adults, booking.children);
    allocate(allocations, ({ allocations: merged }) => {
      setBooking(prev => ({ ...prev, category, allocations: merged }));
      setStep(1);
    });
  };

  const handleBackToWhich = () => {
    setBooking(prev => ({ ...prev, category: null, allocations: [] }));
    setStep(0);
  };

  const handleAddCategory = (
    newCategory: MewsCategoryItem,
    currentAllocations: MewsAllocation[],
    onSuccess: (updated: MewsAllocation[]) => void
  ) => {
    const tempAlloc: MewsAllocation = {
      categoryId: newCategory.id,
      categoryName: newCategory.name,
      pricePerDay: newCategory.pricePerDay,
      quantity: 1,
      guestDistribution: [{ unitIndex: 0, adults: 1, children: 0 }],
    };
    allocate([...currentAllocations, tempAlloc], ({ allocations: merged }) => {
      onSuccess(merged);
    });
  };

  const { createReservationGroup, isPending: bookingPending } = useMewsCreateReservationGroup(locale);

  const handleBookNow = (grandTotal: number) => {
    const request = buildMewsReservationGroupRequest(booking, { ageConfig, user });
    createReservationGroup(request, {
      onSuccess: data => {
        setMewsReservation(data);

        // A comped/free booking is already `Confirmed` — nothing to charge, unchanged path.
        if ((data.status ?? '').toLowerCase() !== 'awaitingpayment') {
          setStep(confirmationStep);
          return;
        }

        const mewsCheckout: MewsCheckoutState = {
          reservationGuid: data.publicGuid!,
          amountMajor: grandTotal,
          currencyCode: 'EUR',
          returnPath: location.pathname,
          booking,
        };

        if (paymentStepIndex !== -1) {
          // Persisted before the step renders, not after: the customer can be redirected away by
          // MB WAY at any point once it does, and this is the only copy that survives that reload.
          rememberCheckout({ kind: 'mews', ...mewsCheckout });
          setPendingPayment({ reservationGuid: mewsCheckout.reservationGuid, amount: grandTotal });
          setStep(paymentStepIndex);
          return;
        }

        const checkoutPath = LinkRouteUrl(context?.settings?.properties?.checkout?.route);
        if (!checkoutPath) {
          // No authored checkout page and no authored payment step means there is nowhere to take
          // payment. The reservation is already held server-side, so show what exists rather than
          // stranding the customer.
          console.warn('BookFlowExperience: awaiting-payment reservation but no checkout route configured.');
          setErrorToastKey(key => key + 1);
          setErrorToastMessage(t('App.Labels.BookingError'));
          setStep(confirmationStep);
          return;
        }

        navigate(checkoutPath, { state: { mewsCheckout } });
      },
      onError: message => {
        console.warn('handleBookNow: reservation group creation failed');
        setErrorToastKey(key => key + 1);
        setErrorToastMessage(message ?? t('App.Labels.BookingError'));
      },
    });
  };

  return (
    <div className="pt-safe-area relative flex h-full flex-col bg-white">
      {isResuming && <SpinLoading wrapperClassName="min-h-screen" />}

      {!isResuming && step === 0 ? (
        <ExperienceBook
          cmsProps={content.properties as BookFlowExperiencesPropertiesModel}
          booking={booking}
          onBookingChange={updates => setBooking(prev => ({ ...prev, ...updates }))}
          onBack={handleBack}
          onContinue={handleNext}
          onRequestSuccess={handleRequestSuccess}
          context={context}
          step={2}
          /* Mews */
          guestsMax={guestsMax}
          ageConfig={ageConfig}
        />
      ) : (
        step === 0.1 &&
        booking?.service?.provider?.toLowerCase() == 'mews' && (
          <MewsWhichPage
            cmsProps={whichProps}
            categories={categoriesLoading ? [] : categories}
            selected={booking.category}
            onContinue={handleContinueFromWhich}
            onBack={handleBack}
            isLoading={allocationPending}
            step={2}
            labels={experienceLabels(booking.service?.serviceType)}
            // The category list here is a choice of treatment, not of tariff — the price is quoted
            // on the rate step that follows, once the occupancy is applied.
            showPrice={false}
            defaultTag={t('App.Labels.NonRefundable')}
            // The design separates the treatments by whitespace; the rule the sunbed list draws
            // would only crowd rows that are already short without a price on them.
            showDivider={false}
          />
        )
      )}
      {!isResuming &&
        steps.map((stepItem, index) => {
          if (index !== step) return null;
          const props = stepItem.content?.properties;
          const stepType = stepItem.content?.contentType;

          if (stepType === PaymentStepContentTypes.Experience) {
            if (!pendingPayment) return null;
            return (
              <MewsPaymentStep
                key={index}
                reservationGuid={pendingPayment.reservationGuid}
                amountMajor={pendingPayment.amount}
                currencyCode="EUR"
                booking={booking}
                context={context}
                onBack={handleBack}
                onSuccess={handlePaid}
                embedded
              />
            );
          }

          switch (stepType) {
            case ExperienceBookFlowExperienceQuantityConfirmationElementContentModelContentType.experienceBookFlowExperienceQuantityConfirmation:
              return (
                <MewsRateAndDistribution
                  cmsProps={props as ExperienceBookFlowExperienceQuantityConfirmationPropertiesModel}
                  booking={booking}
                  categories={categories}
                  ageConfig={ageConfig}
                  guestsMax={guestsMax}
                  onAddCategory={handleAddCategory}
                  isAddingCategory={allocationPending}
                  onBookingChange={updates => setBooking(prev => ({ ...prev, ...updates }))}
                  onBack={handleBack}
                  onContinue={() => setStep(2)}
                  onBackToWhich={handleBackToWhich}
                  step={2}
                  labels={experienceLabels(booking.service?.serviceType)}
                  priceUnitLabel={priceUnitLabel}
                  // The guest count was settled on the What step for a single-occupancy service, so
                  // a children stepper here could only ever read `00`.
                  allowChildren={!mewsServiceIsSingleOccupancy(booking.service?.serviceType)}
                  addCategoryLabel={
                    (props as ExperienceBookFlowExperienceQuantityConfirmationPropertiesModel)
                      ?.addCategorySunbedLabel
                  }
                />
              );
            case ExperienceBookFlowMoreDetailsElementContentModelContentType.experienceBookFlowMoreDetails:
              if (booking?.service?.provider?.toLowerCase() == 'sevenrooms') {
                return (
                  <SevenRoomsDetails
                    key={index}
                    cmsProps={props as ExperienceBookFlowMoreDetailsPropertiesModel}
                    booking={booking}
                    onBookingChange={(updates: Partial<BookingState>) =>
                      setBooking(prev => ({ ...prev, ...updates }))
                    }
                    onBack={handleBack}
                    onSuccess={res => {
                      setReservation(res);
                      setStep(2);
                    }}
                    step={index + 2}
                  />
                );
              } else {
                const moreDetailsProps = props as ExperienceBookFlowMoreDetailsPropertiesModel;
                return (
                  <MewsMoreDetails
                    cmsProps={{
                      bookNowButtonLabel: moreDetailsProps?.bookNowLabel,
                      extraServicesLabel: moreDetailsProps?.personalizeYourBookingLabel,
                    }}
                    booking={booking}
                    onBookingChange={updates => setBooking(prev => ({ ...prev, ...updates }))}
                    onBack={handleBack}
                    onContinue={handleBookNow}
                    isSubmitting={bookingPending}
                    priceUnitLabel={priceUnitLabel}
                    whenLabel={whenLabel}
                    labels={experienceLabels(booking.service?.serviceType)}
                  />
                );
              }
            case ExperienceBookFlowConfirmationElementContentModelContentType.experienceBookFlowConfirmation: {
              const isMews = booking?.service?.provider?.toLowerCase() === 'mews';
              if (isMews) {
                if (!mewsReservation) return null;
                return (
                  <MewsConfirmation
                    key={index}
                    booking={booking}
                    reservation={mewsReservation}
                    onGoHome={() => navigate('/')}
                    onSeeBookingDetails={() =>
                      navigate(
                        `${context?.settings?.properties?.bookingDetail?.route?.path}/${mewsReservation?.publicGuid}`
                      )
                    }
                    labels={experienceLabels(booking.service?.serviceType)}
                    whenLabel={whenLabel}
                  />
                );
              }
              if (!reservation) return null;
              if (booking.isRequestAnyway) {
                return (
                  <SevenRoomsConfirmationRequest
                    key={index}
                    booking={booking}
                    reservation={reservation}
                    onGoHome={() => navigate('/')}
                    onCancelRequest={() => navigate(-1)}
                  />
                );
              }
              return (
                <SevenRoomsConfirmationBook
                  key={index}
                  booking={booking}
                  reservation={reservation}
                  cmsProps={props as ExperienceBookFlowConfirmationPropertiesModel}
                  onGoHome={() => navigate('/')}
                  onSeeBookingDetails={() =>
                    navigate(
                      `${context?.settings?.properties?.bookingDetail?.route?.path}/${reservation.publicGuid}`
                    )
                  }
                />
              );
            }
            default:
              return null;
          }
        })}

      <Toast
        key={errorToastKey}
        variant="error"
        open={errorToastMessage !== null}
        message={errorToastMessage ?? ''}
        onDismiss={() => setErrorToastMessage(null)}
      />
    </div>
  );
}
